import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    const isLocal = window.location.origin.includes("localhost");
    const API_URL = isLocal
        ? "http://localhost:3002/api/v1"
        : "https://www.weekerai.de/api/v1";

    useEffect(() => {
        checkAuth();
    }, []);

    const checkAuth = async () => {
        try {
            // We expect cookies to be sent with this request due to credentials: include (if using fetch/axios)
            // But we need to make sure we use a method that sends credentials.
            // Using standard fetch here.

            const response = await fetch(`${API_URL}/auth/me`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include', // IMPORTANT: Send cookies
            });

            if (response.ok) {
                const data = await response.json();
                if (data.isAuthenticated) {
                    setUser(data.user);
                    setIsAuthenticated(true);
                    if (data.csrfToken) {
                        localStorage.setItem('csrf_token', data.csrfToken);
                    }
                } else {
                    setUser(null);
                    setIsAuthenticated(false);
                }
            } else {
                setUser(null);
                setIsAuthenticated(false);
            }
        } catch (error) {
            console.error("Auth check failed:", error);
            setUser(null);
            setIsAuthenticated(false);
        } finally {
            setIsLoading(false);
        }
    };

    const login = () => {
        // Redirect to Backend Login Endpoint
        window.location.href = `${API_URL}/auth/login`;
    };

    const logout = async () => {
        try {
            const xsrfToken = document.cookie.match(new RegExp('(^| )XSRF-TOKEN=([^;]+)'))?.[2];

            const headers = {};
            if (xsrfToken) {
                headers['X-XSRF-TOKEN'] = decodeURIComponent(xsrfToken);
            }

            await fetch(`${API_URL}/auth/logout`, {
                method: 'POST',
                headers,
                credentials: 'include',
            });
        } catch (error) {
            console.error("Logout failed", error);
        } finally {
            // Always clear local state
            setUser(null);
            setIsAuthenticated(false);
            window.location.href = "/";
        }
    };

    const value = {
        user,
        isAuthenticated,
        isLoading,
        login,
        logout,
    };

    return (
        <AuthContext.Provider value={value}>
            {!isLoading && children}
        </AuthContext.Provider>
    );
};
